/**
 * Material-change audit for client data edited through the wizards.
 *
 * Fact Find / Reverse Fact Find, the Recommendations (Advice) wizard, and the
 * Amendments page all autosave 2s-debounced dot-path diffs through
 * clientStateService.updateClientData. This module watches those diffs for
 * fields in the material areas below and coalesces an editing burst into ONE
 * audit entry per client+area (original → final values), written once editing
 * goes quiet or the tab is hidden/closed. Best-effort like all audit writes —
 * failures never affect saving.
 */
import { addAuditLogEntry } from './firestoreService';
import userTeamService from './userTeamService';
import { auth } from '../firebase';
import { FACT_FIND_SECTIONS } from '../config/navConfig';

const QUIET_MS = 90_000;
const MAX_AGE_MS = 5 * 60_000;
const VALUE_MAX_CHARS = 120;

const FACT_FIND_SECTION_TITLES = Object.fromEntries(
  FACT_FIND_SECTIONS.map((s) => [s.id, s.title]),
);

const ACTION_BY_AREA = {
  fact_find: 'fact_find_updated',
  recommendations: 'recommendations_updated',
  amendments: 'amendments_updated',
};

/** Top-level client-doc key → audit area; everything else is not material
 * (assignment/workflow has its own auditing; fundProjections etc. are caches). */
function areaForTopKey(topKey) {
  if (Object.prototype.hasOwnProperty.call(FACT_FIND_SECTION_TITLES, topKey)) return 'fact_find';
  if (topKey === 'recommendations') return 'recommendations';
  if (topKey === 'amendments') return 'amendments';
  return null;
}

function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

const MAX_LEAF_CHANGES_PER_PATH = 8;
const MAX_DIFF_DEPTH = 4;

function isDrillable(value) {
  return value !== null && typeof value === 'object';
}

/**
 * Drill into changed objects/arrays and collect primitive-level leaf diffs
 * (e.g. `employment.jobs.0.employer`), so an edit inside an array of objects
 * logs the actual changed value rather than "[1 item] → [1 item]". Bounded by
 * depth and leaf count; on overflow the caller falls back to one summary row.
 */
function collectLeafChanges(fromValue, toValue, path, out, depth = 0) {
  if (out.length > MAX_LEAF_CHANGES_PER_PATH) return;
  if (depth < MAX_DIFF_DEPTH && isDrillable(fromValue) && isDrillable(toValue)
    && Array.isArray(fromValue) === Array.isArray(toValue)) {
    const keys = new Set([...Object.keys(fromValue), ...Object.keys(toValue)]);
    for (const key of keys) {
      const from = fromValue[key];
      const to = toValue[key];
      if (safeJson(from) === safeJson(to)) continue;
      collectLeafChanges(from, to, `${path}.${key}`, out, depth + 1);
      if (out.length > MAX_LEAF_CHANGES_PER_PATH) return;
    }
    return;
  }
  out.push({ path, from: fromValue, to: toValue });
}

function safeJson(value) {
  try {
    return JSON.stringify(value) ?? 'undefined';
  } catch {
    return '[unserializable]';
  }
}

function formatValue(value) {
  if (value === undefined || value === null || value === '') return '–';
  if (Array.isArray(value)) return `[${value.length} item${value.length === 1 ? '' : 's'}]`;
  if (typeof value === 'object') {
    const s = safeJson(value);
    return s.length > VALUE_MAX_CHARS ? `${s.slice(0, VALUE_MAX_CHARS)}…` : s;
  }
  const s = String(value);
  return s.length > VALUE_MAX_CHARS ? `${s.slice(0, VALUE_MAX_CHARS)}…` : s;
}

// key `${clientId}|${area}` → bucket
const buckets = new Map();

function flushBucket(key) {
  const bucket = buckets.get(key);
  if (!bucket) return;
  buckets.delete(key);
  if (bucket.timer) clearTimeout(bucket.timer);

  // Drop fields that ended up back at their original value (typed then reverted).
  const changes = [];
  for (const [field, change] of bucket.changes) {
    if (change.fromJson === change.toJson) continue;
    changes.push({ field, from: change.fromFormatted, to: change.toFormatted });
  }
  if (changes.length === 0) return;

  const userEmail = bucket.userId || null;
  addAuditLogEntry(bucket.clientId, {
    userId: userEmail,
    userDisplayName: userEmail ? userTeamService.getUserName(userEmail) : null,
    action: ACTION_BY_AREA[bucket.area],
    details: {
      changes,
      ...(bucket.sections.size > 0 ? { sections: Array.from(bucket.sections) } : {}),
      ...(bucket.scenario ? { scenario: bucket.scenario } : {}),
      ...(userEmail ? {} : { initiatedBy: 'system' }),
    },
  }).catch(() => {});
}

function flushAll() {
  for (const key of Array.from(buckets.keys())) flushBucket(key);
}

function scheduleFlush(key) {
  const bucket = buckets.get(key);
  if (!bucket) return;
  if (bucket.timer) clearTimeout(bucket.timer);
  const remainingMaxAge = bucket.firstAt + MAX_AGE_MS - Date.now();
  const delay = Math.max(0, Math.min(QUIET_MS, remainingMaxAge));
  bucket.timer = setTimeout(() => flushBucket(key), delay);
}

/**
 * Record material changes from an updateClientData call. Must be invoked with
 * `previousData` BEFORE the updates are applied to it; values are snapshotted
 * synchronously so later mutation of the objects cannot alias into the log.
 *
 * `scenario` ({ id, name, live }) marks a Recommendations / Amendments edit made
 * in an advice scenario; each scenario's edits are logged on their own.
 *
 * @param {{ clientId: string, userId?: string|null, updates: object, previousData: object|null, scenario?: object|null }} args
 */
export function recordMaterialChanges({ clientId, userId, updates, previousData, scenario = null }) {
  if (!clientId || !updates || typeof updates !== 'object') return;
  // Autosave paths don't thread the user through; attribute to the signed-in
  // editor instead of falling back to "system".
  const actor = userId || auth.currentUser?.email || null;

  for (const [path, toValue] of Object.entries(updates)) {
    const topKey = path.split('.')[0];
    const area = areaForTopKey(topKey);
    if (!area) continue;

    const fromValue = previousData ? getPath(previousData, path) : undefined;
    if (safeJson(fromValue) === safeJson(toValue)) continue;

    // Prefer primitive-level leaf diffs; fall back to one summary row when the
    // change is too broad to enumerate.
    let leafChanges = [];
    collectLeafChanges(fromValue, toValue, path, leafChanges);
    if (leafChanges.length === 0 || leafChanges.length > MAX_LEAF_CHANGES_PER_PATH) {
      leafChanges = [{ path, from: fromValue, to: toValue }];
    }

    const scenarioId = scenario && scenario.id ? String(scenario.id) : '';
    const key = `${clientId}|${area}|${scenarioId}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        clientId,
        area,
        scenario: scenarioId ? { id: scenarioId, name: scenario.name || null, live: scenario.live === true } : null,
        userId: actor,
        firstAt: Date.now(),
        timer: null,
        changes: new Map(),
        sections: new Set(),
      };
      buckets.set(key, bucket);
    }
    if (actor) bucket.userId = actor;
    if (area === 'fact_find') bucket.sections.add(FACT_FIND_SECTION_TITLES[topKey]);

    for (const leaf of leafChanges) {
      const fromJson = safeJson(leaf.from);
      const toJson = safeJson(leaf.to);
      const existing = bucket.changes.get(leaf.path);
      if (existing) {
        existing.toJson = toJson;
        existing.toFormatted = formatValue(leaf.to);
      } else {
        bucket.changes.set(leaf.path, {
          fromJson,
          toJson,
          fromFormatted: formatValue(leaf.from),
          toFormatted: formatValue(leaf.to),
        });
      }
    }
    scheduleFlush(key);
  }
}

/** Flush everything immediately (used by tests and unload handlers). */
export function flushMaterialChangeAudit() {
  flushAll();
}

// React cleanup doesn't run on hard reload/close — flush pending audit as soon
// as the tab hides, mirroring useOptimizedAutosave's unload handling.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushAll();
  });
}
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushAll);
}
